import { isProbeSuccess } from "./gate0Storage";

/** @typedef {'present' | 'missing' | 'passed' | 'failed' | 'not_run' | 'warning' | 'unavailable'} ReadinessStatus */
/** @typedef {'declared' | 'verified'} ReadinessCheckType */

function connectivityDeclaredPending() {
  return { status: "not_run", value: null };
}

/**
 * @param {Record<string, { value?: unknown, answer?: unknown }>} captured
 * @param {string} itemId
 */
export function captureValue(captured, itemId) {
  const cap = captured?.[itemId];
  if (!cap) return "";
  const raw = cap.value ?? cap.answer ?? "";
  if (raw == null) return "";
  return typeof raw === "string" ? raw.trim() : String(raw).trim();
}

function isPresent(value) {
  if (value == null) return false;
  const s = String(value).trim();
  return s.length > 0 && s !== "—" && s.toLowerCase() !== "n/a";
}

function evidenceEntry(evidenceByLabel, label) {
  if (!evidenceByLabel || typeof evidenceByLabel !== "object") return null;
  return evidenceByLabel[label] || null;
}

function evidenceAnswer(entry) {
  const val = entry?.evidence?.answer_value ?? entry?.evidence?.value ?? null;
  return val == null ? null : String(val).trim() || null;
}

/**
 * Resolve a declared check from Gate 0 fields and/or semantic evidence.
 * Never uses captured map keys (E1, Q8, …) for matching.
 */
function resolveDeclaredValue(label, gate0Value, evidenceByLabel, resolverUnavailable) {
  if (isPresent(gate0Value)) {
    return { status: "present", value: String(gate0Value).trim() };
  }
  const entry = evidenceEntry(evidenceByLabel, label);
  if (resolverUnavailable || entry?.status === "unavailable") {
    return { status: "unavailable", value: null };
  }
  if (entry?.status === "present") {
    const value = evidenceAnswer(entry);
    return isPresent(value) ? { status: "present", value } : { status: "missing", value: null };
  }
  return { status: "missing", value: null };
}

/**
 * Name, version, owner, and deployment environment come from the onboarding
 * project record. A failed semantic resolver does not change them, and an
 * intake-chatbot answer is not a substitute.
 */
function resolveIdentityDeclared(label, storedValue, evidenceByLabel) {
  const entry = evidenceEntry(evidenceByLabel, label);
  const fromDb = entry?.evidence?.source === "db" || entry?.diagnostics?.source === "db";
  if (fromDb && entry?.status === "present") {
    const value = evidenceAnswer(entry);
    if (isPresent(value)) return { status: "present", value };
  }
  if (isPresent(storedValue)) {
    return { status: "present", value: String(storedValue).trim() };
  }
  return { status: "missing", value: null };
}

function semanticOpts(capturedOrSemantic, maybeSemantic) {
  if (maybeSemantic && typeof maybeSemantic === "object") return maybeSemantic;
  if (
    capturedOrSemantic &&
    typeof capturedOrSemantic === "object" &&
    (capturedOrSemantic.evidenceByLabel || capturedOrSemantic.resolverUnavailable)
  ) {
    return capturedOrSemantic;
  }
  return {};
}

/**
 * Build declared System Readiness checks from Gate-0 intake summary data.
 *
 * @param {Record<string, unknown>} gate0Data
 * @param {Record<string, { value?: unknown, answer?: unknown }>} captured
 * @param {{ evidenceByLabel?: Record<string, object>, resolverUnavailable?: boolean }} [semantic]
 * @param {object | null} [systemReport] — current-session system probe report
 */
export function extractSystemReadinessDeclared(
  gate0Data = {},
  captured = {},
  semantic = {},
  systemReport
) {
  const g = gate0Data || {};
  const { evidenceByLabel, resolverUnavailable } = semanticOpts(captured, semantic);
  const systemProbeReport =
    systemReport || g.gate1_readiness_summary || g.api_probe_summary || null;
  const systemOk = isProbeSuccess(systemProbeReport);
  /** @type {Array<{ id: string, label: string, type: ReadinessCheckType, status: ReadinessStatus, value?: string | null }>} */
  const checks = [];

  const versionResolved = resolveIdentityDeclared(
    "System version bundle declared",
    g.app_version || g.version,
    evidenceByLabel
  );
  checks.push({
    id: "version_bundle",
    label: "System version bundle declared",
    type: "declared",
    status: versionResolved.status,
    value: versionResolved.value,
  });

  const environment = resolveIdentityDeclared(
    "Deployment environment declared",
    g.environment,
    evidenceByLabel
  );
  checks.push({
    id: "environment",
    label: "Deployment environment declared",
    type: "declared",
    ...environment,
  });

  const baseUrl = systemOk
    ? resolveDeclaredValue(
        "Base URL declared",
        g.base_url,
        evidenceByLabel,
        resolverUnavailable
      )
    : connectivityDeclaredPending();
  checks.push({
    id: "base_url",
    label: "Base URL declared",
    type: "declared",
    ...baseUrl,
  });

  const endpoints = Array.isArray(g.endpoints) ? g.endpoints : [];
  const validEndpoints = endpoints.filter((ep) => isPresent(ep?.path));
  const endpointsGate0 = validEndpoints.length > 0
    ? `${validEndpoints.length} endpoint(s)`
    : "";
  const endpointsResolved = systemOk
    ? resolveDeclaredValue(
        "API endpoints declared",
        endpointsGate0,
        evidenceByLabel,
        resolverUnavailable
      )
    : connectivityDeclaredPending();
  checks.push({
    id: "endpoints",
    label: "API endpoints declared",
    type: "declared",
    ...endpointsResolved,
  });

  if (systemOk && systemProbeReport?.generated_at) {
    checks.push({
      id: "gate1_system_check",
      label: "Gate 1 system check executed",
      type: "declared",
      status: "present",
      value: `${systemProbeReport.overall_status || "unknown"} · ${new Date(systemProbeReport.generated_at).toLocaleString()}`,
    });
  }

  const hasGate0Integrations =
    Boolean(g.tool_calling) || Boolean(g.mcp_connected) || Boolean(g.multi_agent);
  const depsResolved = resolveDeclaredValue(
    "External dependencies declared",
    hasGate0Integrations ? "Integrations flagged in architecture" : "",
    evidenceByLabel,
    resolverUnavailable
  );
  checks.push({
    id: "dependencies",
    label: "External dependencies declared",
    type: "declared",
    ...depsResolved,
  });

  const integrationParts = [];
  if (g.tool_calling) integrationParts.push("Tool calling");
  if (g.mcp_connected) integrationParts.push("MCP");
  if (g.multi_agent) integrationParts.push("Multi-agent");
  const integrationsResolved = resolveDeclaredValue(
    "Third-party integrations declared",
    integrationParts.join(", "),
    evidenceByLabel,
    resolverUnavailable
  );
  checks.push({
    id: "integrations",
    label: "Third-party integrations declared",
    type: "declared",
    ...integrationsResolved,
  });

  const modelProvider = String(g.model_provider || "").trim();
  const modelName = String(g.model_name || "").trim();
  const modelLabel = [modelProvider, modelName].filter(Boolean).join(" / ");
  const modelResolved = resolveDeclaredValue(
    "Model identity declared",
    modelLabel,
    evidenceByLabel,
    resolverUnavailable
  );
  checks.push({
    id: "model_identity",
    label: "Model identity declared",
    type: "declared",
    ...modelResolved,
  });

  return checks;
}

/**
 * Basic Data Readiness — declared checks from Gate-0 intake summary (no live probes yet).
 *
 * @param {Record<string, unknown>} gate0Data
 * @param {Record<string, { value?: unknown, answer?: unknown }>} captured
 * @param {{ evidenceByLabel?: Record<string, object>, resolverUnavailable?: boolean }} [semantic]
 * @param {object | null} [dataReport] — current-session database probe report
 */
export function extractDataReadinessDeclared(
  gate0Data = {},
  captured = {},
  semantic = {},
  dataReport
) {
  const g = gate0Data || {};
  const { evidenceByLabel, resolverUnavailable } = semanticOpts(captured, semantic);
  const dbProbeReport = dataReport || g.gate1_data_readiness_summary || null;
  const dataOk = isProbeSuccess(dbProbeReport);
  /** @type {Array<{ id: string, label: string, type: ReadinessCheckType, status: ReadinessStatus, value?: string | null }>} */
  const checks = [];

  checks.push({
    id: "data_sources",
    label: "Data / knowledge sources declared",
    type: "declared",
    ...resolveDeclaredValue(
      "Data / knowledge sources declared",
      "",
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  const classificationGate0 = Array.isArray(g.data_classification)
    ? g.data_classification.filter(Boolean).join(", ")
    : String(g.data_classification || "").trim();
  checks.push({
    id: "data_classification",
    label: "Data classification declared",
    type: "declared",
    ...resolveDeclaredValue(
      "Data classification declared",
      classificationGate0,
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  checks.push({
    id: "training_provenance",
    label: "Training data provenance declared",
    type: "declared",
    ...resolveDeclaredValue(
      "Training data provenance declared",
      "",
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  checks.push({
    id: "data_storage",
    label: "Data storage & retention declared",
    type: "declared",
    ...resolveDeclaredValue(
      "Data storage & retention declared",
      "",
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  checks.push({
    id: "evaluation_dataset",
    label: "Evaluation dataset / benchmark declared",
    type: "declared",
    ...resolveDeclaredValue(
      "Evaluation dataset / benchmark declared",
      "",
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  const hasDbDeclaration =
    isPresent(g.db_host) && isPresent(g.db_name || g.db_database) && isPresent(g.db_username);
  const dbGate0 = hasDbDeclaration
    ? `${g.db_type || "postgresql"}://${g.db_host}/${g.db_name || g.db_database}`
    : "";
  checks.push({
    id: "db_connection_declared",
    label: "SQL database connection declared",
    type: "declared",
    ...(dataOk
      ? resolveDeclaredValue(
          "SQL database connection declared",
          dbGate0,
          evidenceByLabel,
          resolverUnavailable
        )
      : connectivityDeclaredPending()),
  });

  if (dataOk && dbProbeReport?.generated_at) {
    checks.push({
      id: "gate1_data_check",
      label: "Gate 1 database check executed",
      type: "declared",
      status: "present",
      value: `${dbProbeReport.overall_status || "unknown"} · ${new Date(dbProbeReport.generated_at).toLocaleString()}`,
    });
  }

  return checks;
}

/**
 * Basic Governance Readiness — declared checks from Gate-0 intake summary (no live probes yet).
 *
 * @param {Record<string, unknown>} gate0Data
 * @param {Record<string, { value?: unknown, answer?: unknown }>} captured
 * @param {{ evidenceByLabel?: Record<string, object>, resolverUnavailable?: boolean }} [semantic]
 */
export function extractGovernanceReadinessDeclared(gate0Data = {}, captured = {}, semantic = {}) {
  const g = gate0Data || {};
  const { evidenceByLabel, resolverUnavailable } = semanticOpts(captured, semantic);
  /** @type {Array<{ id: string, label: string, type: ReadinessCheckType, status: ReadinessStatus, value?: string | null }>} */
  const checks = [];

  checks.push({
    id: "accountable_owner",
    label: "Accountable owner declared",
    type: "declared",
    ...resolveIdentityDeclared(
      "Accountable owner declared",
      g.owner_name,
      evidenceByLabel
    ),
  });

  checks.push({
    id: "governance_board",
    label: "AI governance board declared",
    type: "declared",
    ...resolveDeclaredValue(
      "AI governance board declared",
      "",
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  checks.push({
    id: "escalation_chain",
    label: "Escalation chain declared",
    type: "declared",
    ...resolveDeclaredValue(
      "Escalation chain declared",
      "",
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  checks.push({
    id: "change_approval",
    label: "Change approval process declared",
    type: "declared",
    ...resolveDeclaredValue(
      "Change approval process declared",
      "",
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  const frameworksGate0 = Array.isArray(g.frameworks)
    ? g.frameworks.filter(Boolean).join(", ")
    : String(g.frameworks || "").trim();
  checks.push({
    id: "regulatory_frameworks",
    label: "Regulatory frameworks declared",
    type: "declared",
    ...resolveDeclaredValue(
      "Regulatory frameworks declared",
      frameworksGate0,
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  checks.push({
    id: "audit_logging",
    label: "Audit logging controls declared",
    type: "declared",
    ...resolveDeclaredValue(
      "Audit logging controls declared",
      "",
      evidenceByLabel,
      resolverUnavailable
    ),
  });

  return checks;
}

/**
 * @param {{ score: number, passed: number, total: number }} scoreResult
 * @returns {'ready' | 'partial' | 'not_run' | 'warning'}
 */
export function domainStatusFromDeclaredScore(scoreResult) {
  if (!scoreResult?.total) return "not_run";
  if (scoreResult.score >= 85) return "ready";
  if (scoreResult.score >= 50) return "partial";
  return "warning";
}

/**
 * @param {Array<{ status: ReadinessStatus, type?: ReadinessCheckType }>} checks
 */
export function scoreDeclaredChecks(checks) {
  const declared = checks.filter(
    (c) => c.type === "declared" && c.status !== "unavailable"
  );
  if (declared.length === 0) return { score: 0, passed: 0, total: 0 };
  const passed = declared.filter((c) => c.status === "present").length;
  return {
    score: Math.round((passed / declared.length) * 100),
    passed,
    total: declared.length,
  };
}

/**
 * LLM sometimes flags bearer_jwt/api_key/none as "unsupported" even though
 * the runtime supports them and auth_status is valid. Drop those claims.
 * Only matches when a *supported* type is named — real oauth2/sso flags stay.
 * @param {unknown} text
 */
function isFalseUnsupportedAuthClaim(text) {
  if (text == null) return false;
  const msg = String(text).toLowerCase();
  if (!msg.includes("unsupported") && !msg.includes("not supported")) return false;
  return (
    msg.includes("bearer_jwt") ||
    msg.includes("api_key") ||
    /auth(?:entication)?\s*type['":\s]+none\b/.test(msg)
  );
}

/**
 * @param {string | null | undefined} notes
 */
function scrubFalseUnsupportedAuthNotes(notes) {
  if (!notes) return notes;
  if (isFalseUnsupportedAuthClaim(notes)) return null;
  const parts = String(notes)
    .split(/\s*[·.]\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  const kept = parts.filter((p) => !isFalseUnsupportedAuthClaim(p));
  if (kept.length === parts.length) return notes;
  if (kept.length === 0) return null;
  return kept.join(" · ");
}

/**
 * Merge connectivity report into verified check rows.
 *
 * @param {object | null} report
 */
export function verifiedChecksFromReport(report) {
  if (!report) {
    return [
      {
        id: "ai_endpoint_reachable",
        label: "AI endpoint reachable",
        type: "verified",
        status: "not_run",
        value: null,
      },
      {
        id: "auth_valid",
        label: "Authentication valid",
        type: "verified",
        status: "not_run",
        value: null,
      },
    ];
  }

  const endpoints = Array.isArray(report.endpoints) ? report.endpoints : [];
  const flagsSummary = (report.flags_summary || []).filter(
    (f) => !isFalseUnsupportedAuthClaim(f?.message)
  );
  const anyReachable = endpoints.some((ep) => ep.reachable === true);
  const authOk = endpoints.some(
    (ep) => ep.auth_status === "valid" || ep.auth_status === "not_required"
  );
  const allAuthFailed = endpoints.length > 0 && !authOk;

  /** @type {Array<{ id: string, label: string, type: ReadinessCheckType, status: ReadinessStatus, value?: string | null }>} */
  const checks = [
    {
      id: "ai_endpoint_reachable",
      label: "AI endpoint reachable",
      type: "verified",
      status: anyReachable ? "passed" : "failed",
      value: anyReachable
        ? `${endpoints.filter((e) => e.reachable).length} of ${endpoints.length} reachable`
        : "No reachable endpoints",
    },
    {
      id: "auth_valid",
      label: "Authentication valid",
      type: "verified",
      status: authOk ? "passed" : allAuthFailed ? "failed" : "warning",
      value: endpoints.map((e) => `${e.method || ""} ${e.path || ""}: ${e.auth_status || "unknown"}`).join("; ") || null,
    },
  ];

  endpoints.forEach((ep, i) => {
    const realFlags = (Array.isArray(ep.flags) ? ep.flags : []).filter(
      (f) => !isFalseUnsupportedAuthClaim(f?.message)
    );
    const hasCritical = realFlags.some((f) => f.severity === "critical");
    // Ignore blocking=true when it was only driven by a false "unsupported auth" flag.
    const onlyFalseAuthBlock =
      ep.blocking === true &&
      !hasCritical &&
      ep.reachable !== false &&
      ep.auth_status !== "invalid" &&
      (Array.isArray(ep.flags) ? ep.flags : []).some((f) =>
        isFalseUnsupportedAuthClaim(f?.message)
      );
    const isBlocking = hasCritical || (ep.blocking === true && !onlyFalseAuthBlock);
    const notes = scrubFalseUnsupportedAuthNotes(ep.notes);
    const payloadSuffix = ep.payload_name ? ` [${ep.payload_name}]` : "";
    checks.push({
      id: `endpoint_${ep.endpoint_id || i}${ep.payload_name ? `_${ep.payload_name}` : ""}`,
      label: `${ep.method || "GET"} ${ep.path || ep.endpoint_id || `endpoint ${i + 1}`}${payloadSuffix}`,
      type: "verified",
      status: ep.reachable && !isBlocking ? "passed" : ep.reachable ? "warning" : "failed",
      value: [
        ep.status_code != null ? `HTTP ${ep.status_code}` : null,
        ep.latency_ms != null ? `${ep.latency_ms}ms` : null,
        notes,
      ]
        .filter(Boolean)
        .join(" · ") || null,
    });
  });

  flagsSummary.forEach((flag, i) => {
    checks.push({
      id: `flag_${i}`,
      label: flag.message || "Validation flag",
      type: "verified",
      status: flag.severity === "critical" || flag.severity === "high" ? "failed" : "warning",
      value: flag.severity || null,
    });
  });

  return checks;
}

/**
 * @param {Array<{ status: ReadinessStatus, type?: ReadinessCheckType }>} declared
 * @param {Array<{ status: ReadinessStatus, type?: ReadinessCheckType }>} verified
 */
export function mergeSystemReadinessChecks(declared, verified) {
  return [...declared, ...verified];
}

function postgresReachabilityValue(reachability) {
  const host = reachability.host || "host";
  const port = reachability.port != null ? reachability.port : "";
  const database = reachability.database || "";
  const latency =
    reachability.latency_ms != null ? ` · ${reachability.latency_ms}ms` : "";
  return `postgresql://${host}:${port}/${database}${latency} · confirmed by pgvector connection`;
}

/**
 * SQL database reachability from runDbProbe.
 * Optional postgresReachability shares pgvector connection evidence only —
 * it does not run vector operations through the SQL probe.
 *
 * @param {object | null} report
 * @param {{ postgresReachability?: { connected?: boolean, db_type?: string, host?: string, port?: number, database?: string, latency_ms?: number, source?: string } | null }} [options]
 */
export function verifiedDataChecksFromReport(report, options = {}) {
  const postgresReachability = options.postgresReachability || null;
  const pgvectorConnected = Boolean(postgresReachability?.connected);
  const sqlConnected = Boolean(report?.connected);

  if (!report && !pgvectorConnected) {
    return [
      {
        id: "db_connected",
        label: "Database reachable",
        type: "verified",
        status: "not_run",
        value: null,
      },
    ];
  }

  let dbConnectedStatus = "not_run";
  let dbConnectedValue = null;
  if (sqlConnected) {
    dbConnectedStatus = "passed";
    dbConnectedValue = `${report.db_type || "sql"}://${report.host}:${report.port}/${report.database}${report.latency_ms != null ? ` · ${report.latency_ms}ms` : ""}`;
    if (pgvectorConnected) {
      dbConnectedValue = `${dbConnectedValue} · also confirmed by pgvector`;
    }
  } else if (pgvectorConnected) {
    dbConnectedStatus = "passed";
    dbConnectedValue = postgresReachabilityValue(postgresReachability);
  } else if (report) {
    dbConnectedStatus = "failed";
    dbConnectedValue = report.notes || "Connection failed";
  }

  const checks = [
    {
      id: "db_connected",
      label: "Database reachable",
      type: "verified",
      status: dbConnectedStatus,
      value: dbConnectedValue,
    },
  ];

  if (report?.table_check?.table) {
    checks.push({
      id: "db_table_readable",
      label: `Table ${report.table_check.table} readable`,
      type: "verified",
      status: report.table_check.passed ? "passed" : "failed",
      value:
        report.table_check.passed && report.table_check.row_count != null
          ? `${report.table_check.row_count} row(s)`
          : report.table_check.error || "Table check failed",
    });
  }

  (report?.flags_summary || []).forEach((flag, i) => {
    checks.push({
      id: `db_flag_${i}`,
      label: flag.message || "Database validation flag",
      type: "verified",
      status: flag.severity === "critical" || flag.severity === "high" ? "failed" : "warning",
      value: flag.severity || null,
    });
  });

  return checks;
}

function vectorSearchValue(report) {
  const provider = report.provider || "vector-store";
  const resultCount = report.response?.result_count;
  return [
    provider,
    report.connection === "passed" ? "connected" : null,
    Number.isFinite(Number(resultCount)) ? `${resultCount} hit(s)` : null,
    report.latencyMs != null ? `${report.latencyMs}ms` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Pinecone / MongoDB must run a live search — populated vector_* fields are not enough. */
function isSearchGatedVectorProvider(provider) {
  const t = String(provider || "").trim().toLowerCase();
  return t === "pinecone" || t === "mongodb" || t === "mongo" || t.includes("mongodb");
}

function hasValidVectorSearchResults(report) {
  if (!report || typeof report !== "object") return false;
  if (report.response?.valid === true) return true;
  const resultCount = Number(report.response?.result_count);
  if (Number.isFinite(resultCount) && resultCount >= 1) return true;
  if (report.record?.id) return true;
  return false;
}

function isFlaggedVectorSuccess(report) {
  if (!report || typeof report !== "object") return false;
  if (report.success === true) return true;
  const status = String(report.overall_status || "").toLowerCase();
  return status === "ready" || status === "ready_with_warnings" || status === "pass" || status === "pass_with_warnings";
}

function isPassedVectorSearch(report, provider) {
  if (!isFlaggedVectorSuccess(report)) return false;
  if (isSearchGatedVectorProvider(provider || report.provider)) {
    return hasValidVectorSearchResults(report);
  }
  return true;
}

function vectorStoreDeclarationValue(report) {
  const provider = String(report?.provider || "vector-store").trim().toLowerCase();
  if (provider === "pinecone") {
    const index = report.indexName || report.index_name;
    return isPresent(index) ? `pinecone://${index}` : "pinecone";
  }
  const path = [report.database, report.collection, report.indexName || report.index_name]
    .filter((part) => isPresent(part))
    .join("/");
  return path ? `mongodb://${path}` : "mongodb";
}

/**
 * Declared vector-store check for Pinecone / MongoDB only.
 * Stays not_run until a live similarity search succeeds with valid results.
 * Does not read populated vector_* form fields.
 *
 * @param {object | null} report
 * @param {{ currentProvider?: string }} [options]
 */
export function extractVectorStoreReadinessDeclared(report, options = {}) {
  const provider = String(options.currentProvider || report?.provider || "").trim();
  if (!isSearchGatedVectorProvider(provider)) return [];

  if (!isPassedVectorSearch(report, provider)) {
    return [
      {
        id: "vector_store_declared",
        label: "Vector store connection declared",
        type: "declared",
        status: "not_run",
        value: null,
      },
    ];
  }

  return [
    {
      id: "vector_store_declared",
      label: "Vector store connection declared",
      type: "declared",
      status: "present",
      value: vectorStoreDeclarationValue(report),
    },
  ];
}

/**
 * Vector-store similarity search evidence. Distinct from SQL db_connected.
 * Current reports only; previous/stale reports must be passed separately so they
 * cannot be shown as the active provider's result.
 *
 * Pinecone / MongoDB: not_run until a search is attempted; passed only when the
 * search succeeds with valid results. pgvector / Weaviate keep prior behavior.
 *
 * @param {object | null} report
 * @param {{ previousReport?: object | null, currentProvider?: string }} [options]
 */
export function verifiedVectorStoreChecksFromReport(report, options = {}) {
  const previousReport = options.previousReport || null;
  const currentProvider = String(options.currentProvider || "").trim();
  const checks = [];
  const provider = currentProvider || report?.provider || "";

  if (report) {
    const ok = isPassedVectorSearch(report, provider);
    checks.push({
      id: "vector_store_similarity_search",
      label: "Vector store similarity search",
      type: "verified",
      status: ok ? "passed" : "failed",
      value: vectorSearchValue(report) || report.notes || "Similarity search failed",
    });
  } else if (isSearchGatedVectorProvider(provider)) {
    checks.push({
      id: "vector_store_similarity_search",
      label: "Vector store similarity search",
      type: "verified",
      status: "not_run",
      value: null,
    });
  }

  if (previousReport && previousReport !== report) {
    const previousProvider = previousReport.provider || "vector-store";
    const reason =
      currentProvider && previousProvider !== currentProvider
        ? `previous ${previousProvider} result — not the current ${currentProvider} provider`
        : `previous ${previousProvider} result — configuration changed`;
    checks.push({
      id: "vector_store_similarity_search_previous",
      label: "Vector store similarity search (previous)",
      type: "verified",
      status: "warning",
      value: `${vectorSearchValue(previousReport) || previousReport.notes || "stale"} · ${reason}`,
    });
  }

  return checks;
}

export function mergeDataReadinessChecks(declared, verified) {
  return [...declared, ...verified];
}

/**
 * Combined score: declared present + verified passed count toward total.
 *
 * @param {Array<{ status: ReadinessStatus, type?: ReadinessCheckType }>} checks
 */
export function scoreCombinedChecks(checks) {
  const countable = (checks || []).filter((c) => c.status !== "unavailable");
  if (countable.length === 0) return { score: 0, passed: 0, total: 0 };
  const passed = countable.filter((c) => {
    if (c.type === "declared") return c.status === "present";
    if (c.type === "verified") return c.status === "passed";
    return c.status === "present" || c.status === "passed";
  }).length;
  return {
    score: Math.round((passed / countable.length) * 100),
    passed,
    total: countable.length,
  };
}
